import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { LogOut, FileText, Hash, Clock, Key, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('results');
  
  // Results State
  const [results, setResults] = useState([]);
  const [resultForm, setResultForm] = useState({
    date: new Date().toLocaleDateString('en-GB'),
    frNumber: '',
    srNumber: ''
  });
  const [editingResult, setEditingResult] = useState(null);

  // Common Numbers State
  const [commonNumberForm, setCommonNumberForm] = useState({
    date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-'),
    row1Direct: '46',
    row1House: '1',
    row1Ending: '0',
    row2Direct: '05',
    row2House: '3',
    row2Ending: '5'
  });

  // Password Change State
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!isLoggedIn) {
      navigate('/admin/login');
    }
    
    fetchResults();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    navigate('/admin/login');
  };

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/results/previous?limit=20`);
      setResults(response.data);
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  };

  const handleAddResult = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/results/`, {
        date: resultForm.date,
        firstRound: {
          number: resultForm.frNumber,
          time: '10:30 AM',
          status: 'declared'
        },
        secondRound: {
          number: resultForm.srNumber,
          time: '11:30 AM',
          status: 'declared'
        }
      });
      alert('Result added successfully!');
      setResultForm({ date: new Date().toLocaleDateString('en-GB'), frNumber: '', srNumber: '' });
      fetchResults();
    } catch (error) {
      alert('Error adding result');
    }
  };

  const handleEditResult = (result) => {
    setEditingResult(result);
    setResultForm({
      date: result.date,
      frNumber: result.fr,
      srNumber: result.sr
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdateResult = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/results/`, {
        date: resultForm.date,
        firstRound: {
          number: resultForm.frNumber,
          time: '10:30 AM',
          status: 'declared'
        },
        secondRound: {
          number: resultForm.srNumber,
          time: '11:30 AM',
          status: 'declared'
        }
      });
      alert('Result updated successfully!');
      setResultForm({ date: new Date().toLocaleDateString('en-GB'), frNumber: '', srNumber: '' });
      setEditingResult(null);
      fetchResults();
    } catch (error) {
      alert('Error updating result');
    }
  };

  const handleDeleteResult = async (date) => {
    if (window.confirm(`Delete result for ${date}?`)) {
      try {
        await axios.delete(`${API}/results/${date}`);
        alert('Result deleted!');
        fetchResults();
      } catch (error) {
        alert('Error deleting result');
      }
    }
  };

  const handleUpdateCommonNumber = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/common-number-data/`, commonNumberForm);
      alert('Common Numbers Updated Successfully!\nChanges will appear on the website immediately.');
    } catch (error) {
      alert('Error updating common numbers');
      console.error(error);
    }
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }
    if (passwordForm.oldPassword === 'admin123') {
      alert('Password changed successfully! (Demo only)');
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } else {
      alert('Old password is incorrect');
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Dashboard | Morning Sunday Teer</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-gray-100">
        {/* Header */}
        <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('results')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'results'
                ? 'bg-coral-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FileText className="w-5 h-5" />
            Manage Results
          </button>
          <button
            onClick={() => setActiveTab('common')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'common'
                ? 'bg-coral-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Hash className="w-5 h-5" />
            Common Numbers
          </button>
          <button
            onClick={() => setActiveTab('previous')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'previous'
                ? 'bg-coral-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Clock className="w-5 h-5" />
            Previous Results
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'password'
                ? 'bg-coral-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Key className="w-5 h-5" />
            Change Password
          </button>
        </div>

        {/* Manage Results Tab */}
        {activeTab === 'results' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-6">
              {editingResult ? 'Edit Result' : 'Add/Update Result'}
            </h2>
            <form onSubmit={editingResult ? handleUpdateResult : handleAddResult} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date (DD/MM/YYYY)</label>
                <input
                  type="text"
                  value={resultForm.date}
                  onChange={(e) => setResultForm({...resultForm, date: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="26/07/2026"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">F/R Number</label>
                  <input
                    type="text"
                    value={resultForm.frNumber}
                    onChange={(e) => setResultForm({...resultForm, frNumber: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="16"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">S/R Number</label>
                  <input
                    type="text"
                    value={resultForm.srNumber}
                    onChange={(e) => setResultForm({...resultForm, srNumber: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="92"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-coral-600 hover:bg-coral-700 text-white font-bold py-3 rounded-lg"
                >
                  {editingResult ? 'Update Result' : 'Add/Update Result'}
                </button>
                {editingResult && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingResult(null);
                      setResultForm({ date: new Date().toLocaleDateString('en-GB'), frNumber: '', srNumber: '' });
                    }}
                    className="px-6 bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 rounded-lg"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {/* Common Numbers Tab */}
        {activeTab === 'common' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-6">Update Common Number</h2>
            <form onSubmit={handleUpdateCommonNumber} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Date:</label>
                <input
                  type="text"
                  value={commonNumberForm.date}
                  onChange={(e) => setCommonNumberForm({...commonNumberForm, date: e.target.value})}
                  className="w-full px-4 py-3 border rounded-lg text-lg"
                  placeholder="26-07-2026"
                />
              </div>

              {/* Row 1 */}
              <div>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <label className="block text-lg font-semibold text-gray-700 mb-2">Direct</label>
                    <input
                      type="text"
                      value={commonNumberForm.row1Direct}
                      onChange={(e) => setCommonNumberForm({...commonNumberForm, row1Direct: e.target.value})}
                      className="w-full px-4 py-3 border-2 rounded-lg text-center text-xl font-semibold"
                      placeholder="06,34"
                    />
                  </div>
                  <div className="text-center">
                    <label className="block text-lg font-semibold text-gray-700 mb-2">House</label>
                    <input
                      type="text"
                      value={commonNumberForm.row1House}
                      onChange={(e) => setCommonNumberForm({...commonNumberForm, row1House: e.target.value})}
                      className="w-full px-4 py-3 border-2 rounded-lg text-center text-xl font-semibold"
                      placeholder="4"
                    />
                  </div>
                  <div className="text-center">
                    <label className="block text-lg font-semibold text-gray-700 mb-2">Ending</label>
                    <input
                      type="text"
                      value={commonNumberForm.row1Ending}
                      onChange={(e) => setCommonNumberForm({...commonNumberForm, row1Ending: e.target.value})}
                      className="w-full px-4 py-3 border-2 rounded-lg text-center text-xl font-semibold"
                      placeholder="2"
                    />
                  </div>
                </div>
              </div>

              {/* Row 2 */}
              <div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <input
                      type="text"
                      value={commonNumberForm.row2Direct}
                      onChange={(e) => setCommonNumberForm({...commonNumberForm, row2Direct: e.target.value})}
                      className="w-full px-4 py-3 border-2 rounded-lg text-center text-xl font-semibold"
                      placeholder="89"
                    />
                  </div>
                  <div className="text-center">
                    <input
                      type="text"
                      value={commonNumberForm.row2House}
                      onChange={(e) => setCommonNumberForm({...commonNumberForm, row2House: e.target.value})}
                      className="w-full px-4 py-3 border-2 rounded-lg text-center text-xl font-semibold"
                      placeholder="6"
                    />
                  </div>
                  <div className="text-center">
                    <input
                      type="text"
                      value={commonNumberForm.row2Ending}
                      onChange={(e) => setCommonNumberForm({...commonNumberForm, row2Ending: e.target.value})}
                      className="w-full px-4 py-3 border-2 rounded-lg text-center text-xl font-semibold"
                      placeholder="5"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-lg text-xl"
              >
                Submit
              </button>

              <div className="bg-yellow-50 border-2 border-yellow-400 p-4 rounded-lg mt-6">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> Common numbers are currently hardcoded in the frontend. 
                  This form saves the data for display purposes. To fully integrate, backend updates are needed 
                  in /app/frontend/src/pages/CommonNumbers.jsx
                </p>
              </div>
            </form>
          </div>
        )}

        {/* Previous Results Tab */}
        {activeTab === 'previous' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-6">Previous Results</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-center">First Round</th>
                    <th className="px-4 py-3 text-center">Second Round</th>
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">{result.date}</td>
                      <td className="px-4 py-3 text-center font-bold text-lg">{result.fr}</td>
                      <td className="px-4 py-3 text-center font-bold text-lg">{result.sr}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={() => {
                              setActiveTab('results');
                              handleEditResult(result);
                            }}
                            className="flex items-center gap-1 bg-gray-700 hover:bg-gray-800 text-white px-3 py-2 rounded text-sm font-medium"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteResult(result.date)}
                            className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm font-medium"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Change Password Tab */}
        {activeTab === 'password' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-6">Change Password</h2>
            <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Old Password</label>
                <input
                  type="password"
                  value={passwordForm.oldPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, oldPassword: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-coral-600 hover:bg-coral-700 text-white font-bold py-3 rounded-lg"
              >
                Change Password
              </button>
            </form>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default AdminDashboard;